export const SIDE_EVENTS_HOST = "side-events.chiefintech.com";
export const SIDE_EVENTS_ORIGIN = `https://${SIDE_EVENTS_HOST}`;
export const SIDE_EVENTS_INTERNAL_PATH = "/companion/events-overview";
export const PUBLIC_COMPANION_ENTITY_SLUG_PATTERN = "^[a-z0-9]+(-[a-z0-9]+)*$";
const PUBLIC_COMPANION_ENTITY_SLUG_REGEX = new RegExp(PUBLIC_COMPANION_ENTITY_SLUG_PATTERN);

/** Normalize only canonical public entity slugs; reject empty or legacy-corrupt values. */
export function validCompanionEntitySlug(value: unknown): string | null {
  if (typeof value !== "string" || value !== value.trim()) return null;
  const slug = value;
  return slug.length <= 200 && PUBLIC_COMPANION_ENTITY_SLUG_REGEX.test(slug)
    ? slug
    : null;
}

const COMPANION_COLLECTION_PATHS = [
  /^\/popular\/?$/,
  /^\/speakers(?:\/popular)?\/?$/,
  /^\/day\/(?:[1-9]|10)\/?$/,
  /^\/(?:c|for|access|venue|topic|roles|company|people)\/[^/?#]+\/?$/,
];

/** Match only the exact public collection URL shapes, without surplus segments. */
export function isCompanionCollectionPath(pathname: string): boolean {
  return COMPANION_COLLECTION_PATHS.some((pattern) => pattern.test(pathname));
}

/** Emit only conference-day pages that contain at least one published event. */
export function companionDayCollectionPaths(
  daySets: Array<number[] | null | undefined>,
): string[] {
  const days = new Set<number>();
  for (const values of daySets) {
    for (const value of values || []) {
      if (Number.isInteger(value) && value >= 1 && value <= 10) days.add(value);
    }
  }
  return Array.from(days).sort((a, b) => a - b).map((day) => `/day/${day}`);
}

export interface CompanionApplicationTarget {
  slug: string;
  accessType?: string | null;
  rsvpUrl?: string | null;
  registrationUrl?: string | null;
}

/** Return the server-owned application path only when this exact event opts in. */
export function authorizedCompanionApplicationPath(
  event: CompanionApplicationTarget,
  subdomain: string,
): string | null {
  if (event.accessType !== "application_required") return null;
  const canonicalPath = `/companion/${subdomain}/apply/${event.slug}`;
  const expected = new Set([
    `/apply/${event.slug}`,
    canonicalPath,
  ]);
  const hasAuthorizedTarget = [event.rsvpUrl, event.registrationUrl].some((value) => {
    const candidate = value?.trim();
    if (!candidate || !/^\/(?![\\/])/.test(candidate)) return false;
    try {
      const parsed = new URL(candidate, "https://companion.invalid");
      const path = decodeURIComponent(parsed.pathname).replace(/\/$/, "");
      const allowedQuery = Array.from(parsed.searchParams.entries()).every(([key, value]) => (
        key === "year" && /^\d{4}$/.test(value) && validCompanionYear(Number(value)) !== null
      ));
      return parsed.origin === "https://companion.invalid" && !parsed.hash && allowedQuery && expected.has(path);
    } catch {
      return false;
    }
  });
  return hasAuthorizedTarget ? canonicalPath : null;
}

/**
 * Internal application forms are opt-in DB configuration, not an implication
 * of an event being published. The stored CTA must point at this exact event.
 */
export function isAuthorizedCompanionApplication(
  event: CompanionApplicationTarget,
  subdomain: string,
): boolean {
  return authorizedCompanionApplicationPath(event, subdomain) !== null;
}

export interface CompanionEditionIdentity {
  subdomain: string;
  year?: number | null;
}

export function validCompanionYear(year: number | null | undefined): number | null {
  return Number.isInteger(year) && Number(year) >= 2000 && Number(year) <= 2200
    ? Number(year)
    : null;
}

/** Parse an optional HTTP query value without falling back on malformed years. */
export function parseCompanionYearQuery(value: unknown): number | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== "string" || !/^\d{4}$/.test(value)) return -1;
  return validCompanionYear(Number(value)) ?? -1;
}

export const INVALID_COMPANION_YEAR = -1;

/** Keep every edition-scoped URL stable when one companion subdomain has multiple years. */
export function withCompanionYear(href: string, year: number | null | undefined): string {
  const normalizedYear = validCompanionYear(year);
  if (normalizedYear === null) return href;

  const absolute = /^https?:\/\//i.test(href);
  const parsed = new URL(href, "https://companion.invalid");
  parsed.searchParams.set("year", String(normalizedYear));
  return absolute
    ? parsed.toString()
    : `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function companionEditionUrl(
  edition: CompanionEditionIdentity,
  path = "/",
): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const base = `https://www.chiefinai.com/companion/${encodeURIComponent(edition.subdomain)}`;
  return withCompanionYear(`${base}${normalizedPath}`, edition.year);
}

export function companionEditionHref(
  path: string,
  year: number | null | undefined,
): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return withCompanionYear(normalizedPath, year);
}

export const SIDE_EVENTS_GROWTH_PATHS = [
  {
    id: "executive-membership",
    role: "Executives and members",
    brand: "Chief in Tech",
    title: "Stay connected beyond conference week",
    description: "Explore the executive membership and community path. Joining is a separate, voluntary step.",
    cta: "Explore executive membership",
    href: "https://www.chiefintech.com/membership?utm_source=side-events.chiefintech.com&utm_medium=referral&utm_campaign=conference-side-events&utm_content=executive-membership",
  },
  {
    id: "founder-fit",
    role: "Founders",
    brand: "Chief in Tech Capital",
    title: "Check the fund's founder fit",
    description: "Review the public investment focus and process before choosing whether to submit a pitch.",
    cta: "See founder investment fit",
    href: "https://chiefintech.capital/fund/for-founders?utm_source=side-events.chiefintech.com&utm_medium=referral&utm_campaign=conference-side-events&utm_content=founder-fit",
  },
  {
    id: "investor-community",
    role: "Investors and LPs",
    brand: "Chief in Tech Capital",
    title: "Explore the investor community",
    description: "Review the LP and community path, then opt in if it matches how you want to participate.",
    cta: "Explore LP and investor paths",
    href: "https://chiefintech.capital/fund/community?role=lp&utm_source=side-events.chiefintech.com&utm_medium=referral&utm_campaign=conference-side-events&utm_content=investor-community",
  },
  {
    id: "ecosystem-partner",
    role: "Partners and organizers",
    brand: "Chief in Tech Capital",
    title: "Propose one relevant collaboration",
    description: "Use the human-reviewed ecosystem partner path. Interest does not create a partnership or data exchange.",
    cta: "Explore the partner path",
    href: "https://chiefintech.capital/fund/community?role=ecosystem_partner&utm_source=side-events.chiefintech.com&utm_medium=referral&utm_campaign=conference-side-events&utm_content=ecosystem-partner",
  },
  {
    id: "speaker-network",
    role: "Speakers",
    brand: "Chief in AI",
    title: "Find and claim a listed speaker profile",
    description: "Browse Web Summit speaker profiles. A matching, verified account can claim only its own listed profile.",
    cta: "Browse Web Summit speakers",
    href: "https://www.chiefinai.com/companion/websummit/speakers?utm_source=side-events.chiefintech.com&utm_medium=referral&utm_campaign=conference-side-events&utm_content=speaker-network",
  },
] as const;
